import { useQuery, gql } from '@apollo/client';
import React, { useMemo } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { hrefFor } from '../../routers';
import { Loading } from '../../components/Loading';
import { ModuleSection, MODULE_SECTION_FRAGMENT } from '../../components/ModuleSection';
import { CustomPageQuery, CustomPageQueryVariables } from '../../generated/types/CustomPageQuery';
import { findSectionModules } from '../../lib/findSectionModules';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { useCustomPageVariables } from '../../hooks/useCustomPageVariables';
import { NotFoundPage } from '../NotFoundPage';

export const CUSTOM_PAGE_QUERY = gql`
  query CustomPageQuery(
    $slug: String!
    $countryCode: String
    $variation: Boolean
    $versionId: Int
  ) {
    displayPage(
      slug: $slug
      country_code: $countryCode
      variation: $variation
      version_id: $versionId
    ) {
      name
      targetBuckets {
        name
        modules {
          ...ModuleSectionFragment
        }
      }
    }
  }
  ${MODULE_SECTION_FRAGMENT}
`;

interface Props {
  variation?: boolean;
}

export const HomePage: React.FC<Props> = ({ variation = false }) => {
  const customPageVariables = useCustomPageVariables();
  const { vertical } = useParams<{ vertical?: string }>();

  // NOTICE: The variation variable is passed in from a feature flag and used to query SearchAPI for the variation version
  const { data, loading, error } = useQuery<CustomPageQuery, CustomPageQueryVariables>(
    CUSTOM_PAGE_QUERY,
    {
      variables: {
        ...customPageVariables,
        variation: variation
      }
    }
  );

  const topModules = useMemo(() => findSectionModules(data, 'top'), [data]);
  const headerModules = useMemo(() => findSectionModules(data, 'header'), [data]);
  const bottomModules = useMemo(() => findSectionModules(data, 'bottom'), [data]);

  if (error) throwQueryError(error);

  if (vertical === 'men') {
    return <Redirect to={hrefFor.HomePage()} />;
  }

  if (data && !data.displayPage) return <NotFoundPage />;

  return (
    <>
      {loading && <Loading />}
      {topModules && <ModuleSection modules={topModules.modules} />}
      {headerModules && <ModuleSection modules={headerModules.modules} />}
      {bottomModules && <ModuleSection modules={bottomModules.modules} />}
    </>
  );
};
